import type { GameSymbol } from '../game/symbols'
import { log } from '../log'
import type { ResolvedTheme } from './resolve'
import { resolveTheme } from './resolve'
import { ThemeSchema } from './schema'

/**
 * Theme loading.
 *
 * Themes are fetched at runtime, not bundled: a designer edits
 * `themes/<id>/theme.json`, reloads, and sees the change — no rebuild, no
 * developer in the loop. That is the whole point of the pipeline.
 *
 * Every failure here is non-fatal. A missing index, a 404, a malformed
 * manifest — all of it degrades to the generated placeholder and logs a
 * warning. A broken skin must never take the game down with it: the money
 * path does not depend on which colour a symbol is.
 *
 * No Pixi in this module — the lobby uses it to colour previews and should
 * not drag a renderer in. Textures are attached separately (`assets.ts`).
 */

/** Where the dev server and the build both expose the `themes/` directory. */
const THEMES_BASE = '/themes/'

interface ThemeIndex {
  /** gameId → theme id. A game with no entry renders as placeholder. */
  byGame: Record<string, string>
}

let indexPromise: Promise<ThemeIndex | null> | null = null

/** The index is fetched once per session and shared by every caller. */
function loadIndex(): Promise<ThemeIndex | null> {
  indexPromise ??= fetchJson(`${THEMES_BASE}index.json`)
    .then((data) => {
      const byGame = (data as ThemeIndex | null)?.byGame
      return byGame && typeof byGame === 'object' ? { byGame } : null
    })
    .catch((err: unknown) => {
      log.warn('theme.index-unreadable', 'every game falls back to a placeholder skin', {
        url: `${THEMES_BASE}index.json`,
        reason: err instanceof Error ? err.message : String(err),
      })
      return null
    })
  return indexPromise
}

async function fetchJson(url: string): Promise<unknown> {
  const res = await fetch(url, { cache: 'no-cache' })
  if (!res.ok) throw new Error(`${url} → ${res.status}`)
  return res.json()
}

/**
 * Resolve the theme for one game.
 *
 * Always returns a usable theme — the placeholder when there is nothing
 * better — so callers never branch on "themed or not".
 */
export async function loadTheme(
  gameId: string,
  symbols: readonly GameSymbol[],
): Promise<ResolvedTheme> {
  const index = await loadIndex()
  const themeId = index?.byGame[gameId]
  if (!themeId) return resolveTheme(null, symbols)

  const dir = `${THEMES_BASE}${themeId}/`
  try {
    const parsed = ThemeSchema.safeParse(await fetchJson(`${dir}theme.json`))
    if (!parsed.success) {
      // Loud, but not fatal: the designer sees exactly which field is wrong
      // while the game keeps running on the placeholder.
      log.warn('theme.invalid', `"${themeId}" failed validation, using the placeholder`, {
        themeId,
        gameId,
        issues: parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`),
      })
      return resolveTheme(null, symbols)
    }
    if (!parsed.data.games.includes(gameId)) {
      log.warn('theme.game-not-listed', `"${themeId}" does not list this game in games[]`, {
        themeId,
        gameId,
      })
      return resolveTheme(null, symbols)
    }
    return resolveTheme(parsed.data, symbols, dir)
  } catch (err) {
    log.warn('theme.load-failed', `"${themeId}" did not load, using the placeholder`, {
      themeId,
      gameId,
      reason: err instanceof Error ? err.message : String(err),
    })
    return resolveTheme(null, symbols)
  }
}

/** Themes for a whole lobby, in parallel. Used to colour game previews. */
export async function loadThemes(
  games: readonly { gameId: string; symbols: GameSymbol[] }[],
): Promise<Record<string, ResolvedTheme>> {
  const entries = await Promise.all(
    games.map(async (g) => [g.gameId, await loadTheme(g.gameId, g.symbols)] as const),
  )
  return Object.fromEntries(entries)
}

/** Test seam: forget the cached index so a reload re-reads it. */
export function resetThemeCache(): void {
  indexPromise = null
}
